import { useCallback, useEffect, useRef, useState } from 'react';
import { rectAtPoint } from './templateLayout';

// Screen pixels the pointer travels before a press on a palette entry becomes
// a drag; anything less is a click.
const DRAG_THRESHOLD_PX = 5;

// Carrying a palette entry onto the page. The press is followed on the window
// rather than with HTML drag and drop, so the page can show exactly where the
// box will land — snapped to the grid, at the size it will be — for as long as
// the pointer is over it, and so it works with a finger as well as a mouse.
//
//   const { drag, itemProps, shouldIgnoreClick } = usePaletteDrag({ ... });
//   <button {...itemProps(source)} onClick={() => !shouldIgnoreClick() && add(source)} />
//
// `sizeOf(source)` is how big the box will be, `onDrop(source, rect)` places
// it. A press that never became a drag is left to the button's own click.
export const usePaletteDrag = ({ pageRef, scale, grid, enabled, sizeOf, onDrop }) => {
  const [drag, setDrag] = useState(null);
  const pressRef = useRef(null);
  const cleanupRef = useRef(null);
  const ignoreClickRef = useRef(false);
  // The handlers below outlive the render that attached them.
  const latest = useRef({});
  latest.current = { scale, grid, sizeOf, onDrop };

  const stop = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    pressRef.current = null;
    document.body.style.cursor = '';
    setDrag(null);
  }, []);

  useEffect(() => stop, [stop]);

  const landingRect = (event, press) => {
    const bounds = pageRef.current?.getBoundingClientRect();
    if (!bounds) return null;
    const inside =
      event.clientX >= bounds.left &&
      event.clientX <= bounds.right &&
      event.clientY >= bounds.top &&
      event.clientY <= bounds.bottom;
    if (!inside) return null;
    const { scale: currentScale, grid: currentGrid } = latest.current;
    const point = {
      x: (event.clientX - bounds.left) / currentScale,
      y: (event.clientY - bounds.top) / currentScale,
    };
    return rectAtPoint(point, press.size, event.altKey ? 0 : currentGrid);
  };

  const begin = (source, event) => {
    if (!enabled || event.button !== 0) return;
    // Keeps the press from selecting text on its way across the screen.
    event.preventDefault();
    stop();
    pressRef.current = {
      source,
      size: latest.current.sizeOf(source),
      startX: event.clientX,
      startY: event.clientY,
      active: false,
      rect: null,
    };

    const onMove = (moveEvent) => {
      const press = pressRef.current;
      if (!press) return;
      if (!press.active) {
        const travelled = Math.hypot(moveEvent.clientX - press.startX, moveEvent.clientY - press.startY);
        if (travelled < DRAG_THRESHOLD_PX) return;
        press.active = true;
        document.body.style.cursor = 'grabbing';
      }
      press.rect = landingRect(moveEvent, press);
      setDrag({ source: press.source, x: moveEvent.clientX, y: moveEvent.clientY, rect: press.rect });
    };
    const onUp = () => {
      const press = pressRef.current;
      stop();
      if (!press?.active) return;
      // The release may land back on the entry it started from, which would
      // click it and add a second box.
      ignoreClickRef.current = true;
      window.setTimeout(() => {
        ignoreClickRef.current = false;
      }, 0);
      if (press.rect) latest.current.onDrop(press.source, press.rect);
    };
    const onKeyDown = (keyEvent) => {
      if (keyEvent.key === 'Escape') stop();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', stop);
    window.addEventListener('keydown', onKeyDown);
    cleanupRef.current = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', stop);
      window.removeEventListener('keydown', onKeyDown);
    };
  };

  const itemProps = (source) => ({ onPointerDown: (event) => begin(source, event) });
  const shouldIgnoreClick = () => ignoreClickRef.current;

  return { drag, itemProps, shouldIgnoreClick };
};
